import { useRef, useEffect, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { usePostHog } from "posthog-js/react";
import { toast } from "sonner";
import { useAuth } from "../hooks/useAuth";

export function GoogleLoginButton() {
  const { loginWithGoogle } = useAuth();
  const posthog = usePostHog();
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [buttonWidth, setButtonWidth] = useState(320);

  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setButtonWidth(Math.min(width, 400));
      }
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="space-y-3">
      {isLoading ? (
        <div className="flex items-center justify-center h-10 rounded bg-muted/50">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Signing in...
          </div>
        </div>
      ) : (
        <GoogleLogin
          onSuccess={async (response) => {
            posthog.capture("auth:google_signin_button_click");
            if (response.credential) {
              try {
                setIsLoading(true);
                await loginWithGoogle(response.credential);
                posthog.capture("auth:google_signin_complete", { is_success: true });
              } catch (err) {
                const errorMessage = err instanceof Error ? err.message : "Login failed";
                posthog.capture("auth:google_signin_complete", {
                  is_success: false,
                  error_code: errorMessage,
                });
                toast.error(errorMessage);
                setIsLoading(false);
              }
            }
          }}
          onError={() => {
            posthog.capture("auth:google_signin_complete", {
              is_success: false,
              error_code: "cancelled",
            });
            toast.error("Google sign-in was cancelled or failed");
          }}
          theme="filled_black"
          size="large"
          width={buttonWidth}
        />
      )}
    </div>
  );
}
